import Box from '@mui/material/Box';
import { PopoverTriggerButton } from '@openx/components-core/lib/Button/PopoverTriggerButton.tsx';
import { Ellipsis } from '@openx/components-core/lib/Ellipsis/Ellipsis.tsx';
import { FiltersPopover } from '@openx/components-core/lib/FiltersPopover/FiltersPopover.tsx';
import { type DealInsightsDealNameOrIdOptions, dealInsightsFilterOptionKeys } from '@openx/types/dealInsights.ts';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { dealInsightsStateFields } from '../../../constants.ts';
import { useDealInsightsContext } from '../../../context/DealInsightsContext.tsx';
import { DealNameOrIdAutocomplete } from './DealNameOrIdAutocomplete.tsx';

type DealNameOrIdPopoverProps = {
  filterOptions: DealInsightsDealNameOrIdOptions;
  loading: boolean;
};

const DEAL_NAME_FILTER_MAX_WIDTH = 240;

export const DealNameOrIdPopover = ({ filterOptions, loading }: DealNameOrIdPopoverProps) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const { setValue, watch, getValues } = useFormContext();
  const { filters: contextFilters, setFilters: setContextFilters } = useDealInsightsContext();

  const formKey = `${dealInsightsStateFields.FILTERS}.${dealInsightsFilterOptionKeys.DEAL}`;

  const observableLastOpenedFilterKey = watch(dealInsightsStateFields.LAST_OPENED_FILTER_KEY);
  const preventLoading = observableLastOpenedFilterKey === dealInsightsFilterOptionKeys.DEAL;

  const observableFormValue = watch(formKey);
  const contextValue = contextFilters.deal;

  const hasChanges = JSON.stringify(observableFormValue) !== JSON.stringify(contextValue);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    if (hasChanges) {
      setValue(formKey, contextValue);
    }
    setAnchorEl(null);
  };

  const handleApply = () => {
    setContextFilters({
      ...contextFilters,
      [dealInsightsFilterOptionKeys.DEAL]: observableFormValue,
    });
    setAnchorEl(null);
  };

  const handleClear = () => {
    setValue(formKey, []);
    setContextFilters({
      ...contextFilters,
      [dealInsightsFilterOptionKeys.DEAL]: [],
    });
    setAnchorEl(null);
  };

  const renderButtonText = useCallback(() => {
    if (contextValue.length === 0) {
      return t('Deal Name or ID');
    }

    if (filterOptions[dealInsightsFilterOptionKeys.DEAL].length > 0 && contextValue.length === 1) {
      const foundOption = filterOptions[dealInsightsFilterOptionKeys.DEAL].find(
        option => option.ox_deal_id === contextValue[0].ox_deal_id
      );
      if (foundOption) {
        return foundOption.name;
      }
    }

    if (contextValue.length === 1) {
      return contextValue[0].name;
    }

    return contextValue[0].name;
  }, [contextValue, filterOptions, t]);

  return (
    <>
      <PopoverTriggerButton
        data-test="deal-name-or-id-button"
        onClick={handleOpen}
        isEmpty={contextValue.length === 0}
        isOpen={isOpen}
        sx={{
          maxWidth: DEAL_NAME_FILTER_MAX_WIDTH,
          minWidth: 0,
          '& > div': {
            minWidth: 0,
          },
          '& > svg': {
            flexShrink: 0,
          },
        }}
      >
        {contextValue.length > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Ellipsis width="100%">{renderButtonText()}</Ellipsis>
            </Box>
            {contextValue.length > 1 && (
              <Box component="span" sx={{ flexShrink: 0, ml: 0.5 }}>
                {` +${contextValue.length - 1}`}
              </Box>
            )}
          </Box>
        ) : (
          renderButtonText()
        )}
      </PopoverTriggerButton>

      <FiltersPopover
        open={isOpen}
        onClose={handleClose}
        onApply={handleApply}
        onClear={handleClear}
        title={t('Deal Name or ID')}
        anchorEl={anchorEl}
        slotProps={{
          paper: {
            sx: { width: '360px' },
          },
        }}
        isClearable={getValues(formKey).length > 0}
        isApplyable={hasChanges}
        data-test="deal-name-or-id-popover-box"
      >
        <DealNameOrIdAutocomplete
          name={formKey}
          disabled={loading && !preventLoading}
          options={filterOptions[dealInsightsFilterOptionKeys.DEAL]}
          loading={loading && !preventLoading}
          onOpen={() => setValue(dealInsightsStateFields.LAST_OPENED_FILTER_KEY, dealInsightsFilterOptionKeys.DEAL)}
        />
      </FiltersPopover>
    </>
  );
};
